import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { useMeasurementSignals } from '../../StateManagment/MeasurementSignalsContext'
import selectUpcomingSignals from '../../helpers/measurementSignals/selectUpcomingSignals'
import useMomentMatrixColumns from '../../hooks/measurementSignals/useMomentMatrixColumns'
import {
    MOMENT_STATUS_LABELS,
    MOMENT_STATUS_MARKS,
} from '../../helpers/measurementSignals/describeSignal'
import daysUntilDue from '../../helpers/measurementSignals/daysUntilDue'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import StaticCardLabel from './StaticCardLabel'
import UpcomingMomentCell from './UpcomingMomentCell'
import { buildShareQuestionnairePath } from '../../helpers/questionnaires/shareQuestionnairePath'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/meetmoment-signalering.md

/** De twee kolommen vóór de meetmomenten. Deelnemers én huishoudens lopen door dezelfde lijst. */
const LEADING_COLUMNS = [
    { key: 'who', label: 'DEELNEMER OF HUISHOUDEN' },
    { key: 'research', label: 'ONDERZOEK' },
]

/**
 * De vier toestanden in de legenda, op volgorde van 'klaar' naar 'weet ik niet'.
 *
 * Woorden én tekens komen uit dezelfde bron als de tekstballon bij een vakje.
 * Anders staat er in de legenda een andere naam voor hetzelfde vakje dan de
 * tekstballon geeft, en dan lijkt het om twee dingen te gaan.
 */
const LEGEND_STATUSES = ['filled', 'missing', 'missed', 'unknown']

/** En de knop erachter. */
const TRAILING_COLUMNS = [
    { key: 'action', label: '', searchable: false },
]

/**
 * Het detailscherm van een deelnemer of huishouden. De twee soorten lopen door
 * dezelfde lijst, dus de link moet per rij bepaald worden.
 *
 * @param {{entityKind: string, entityId: string}} row
 * @returns {string}
 */
const detailPath = (row) => (row.entityKind === 'adress'
    ? `/${client}/adressdetails/${row.entityId}`
    : `/${client}/personabuilder/${row.entityId}`)

/**
 * Wie is er toe aan een volgende meting, en staat de vorige er wel?
 *
 * Elke meting krijgt een eigen kolom, zodat één blik over de rij volstaat:
 * groen is binnen, rood staat nog open. Bij het eerstvolgende openstaande
 * vakje staat over hoeveel dagen die meting de deur uit moet.
 *
 * Er valt niets in te stellen: de lijst volgt uit wie aan welk onderzoek is
 * gekoppeld en wat er al is ingevuld. Langst wachtende bovenaan.
 */
const DashboardUpcomingMomentsCard = () => {
    const { rows, loaded, unavailable } = useMeasurementSignals()

    const items = useMemo(() => selectUpcomingSignals(rows), [rows])

    const { momentLabels, columns } = useMomentMatrixColumns({
        items, leading: LEADING_COLUMNS, trailing: TRAILING_COLUMNS,
    })

    // Stabiel, anders vervalt de zoekfilter-memo in Table bij elke render.
    const searchAccessor = useCallback(
        (row) => `${row?.entityName || ''} ${row?.researchTitle || ''}`,
        [],
    )

    // Drie toestanden, en ze mogen niet door elkaar lopen. "Niets openstaand"
    // is een bewering aan de lezer, en die is onwaar zolang de gegevens
    // onderweg zijn of een read is geweigerd.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) {
        return (
            <div>
                <StaticCardLabel title='Aankomende meetmomenten' />
                <p className='score-explain-card__placeholder'>Laden…</p>
            </div>
        )
    }

    return (
        <div>
            <StaticCardLabel title='Aankomende meetmomenten' />

            {unavailable.length > 0 && (
                <p className='measurement-signal-warning'>
                    Dit overzicht is onvolledig — {unavailable.join(', ')} kon niet gelezen
                    worden. Er kunnen meer mensen toe zijn aan een meting dan hier staan.
                </p>
            )}

            {items.length === 0 && unavailable.length === 0 ? (
                <p className='measurement-signal-note'>
                    Niemand is op dit moment toe aan een volgende meting. Deelnemers en
                    huishoudens verschijnen hier zodra een onderzoek voor hen geldt: via de
                    schakelaar op het onderzoek, of met de hand gekoppeld.
                </p>
            ) : (
                <>
                    <p className='moment-matrix-legend'>
                        {LEGEND_STATUSES.map((status) => (
                            <span key={status} className='moment-matrix-legend-item'>
                                <span className={`moment-matrix-dot moment-matrix-dot--${status}`}>
                                    {MOMENT_STATUS_MARKS[status]}
                                </span>
                                {MOMENT_STATUS_LABELS[status]}
                            </span>
                        ))}
                    </p>
                    <Table
                        columns={columns}
                        items={items}
                        getItemKey={(row) => row.key}
                        searchAccessor={searchAccessor}
                        searchPlaceholder='Zoeken op naam of onderzoek'
                        emptyMessage={unavailable.length > 0
                            ? 'Onbekend — een deel van de gegevens kon niet gelezen worden.'
                            : 'Geen meetmomenten die aandacht vragen.'}
                        renderRow={(row) => {
                            // Eén keer per rij: alleen het eerstvolgende vakje
                            // gebruikt hem, en elke aanroep had anders zijn
                            // eigen `Date.now()`.
                            const dueDays = daysUntilDue(row)

                            return (
                            <>
                                <TableCell>
                                    <Link to={detailPath(row)}>{row.entityName || 'Naamloos'}</Link>
                                    {row.startEstimated && (
                                        <span className='measurement-signal-note'>startdatum geschat</span>
                                    )}
                                    {/* De matrix laat een overgeslagen meting wél zien (groen
                                        rechts van rood), maar alleen als patroon. Zonder deze
                                        regel staat er nergens meer in woorden dat er iets
                                        mis is. */}
                                    {row.hasGap && (
                                        <span className='measurement-signal-note measurement-signal-note--warning'>
                                            Let op: een latere meting is al ingevuld
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell>{row.researchTitle || 'Naamloos onderzoek'}</TableCell>
                                {momentLabels.map((label, index) => {
                                    const moment = row.moments?.[index] || null
                                    return (
                                        <TableCell key={`${row.key}-moment-${index}`} className='moment-matrix-cell-wrap'>
                                            <UpcomingMomentCell
                                                moment={moment}
                                                isNext={Boolean(moment) && row.next?.ID === moment.ID}
                                                dueDays={dueDays}
                                            />
                                        </TableCell>
                                    )
                                })}
                                <TableCell>
                                    <span className='measurement-signal-actions'>
                                        {row.questionnaireId ? (
                                            <Link to={buildShareQuestionnairePath({ client, questionnaireID: row.questionnaireId, researchID: row.researchId, respondentID: row.entityId })}>
                                                Vragenlijst delen
                                            </Link>
                                        ) : (
                                            <span className='measurement-signal-note'>Geen vragenlijst</span>
                                        )}
                                    </span>
                                </TableCell>
                            </>
                            )
                        }}
                    />
                </>
            )}
        </div>
    )
}

export default DashboardUpcomingMomentsCard
